/* The audit chain over the real API. No `X-Lumiq-Tenant` header — hard rule 1.
 *
 * **Reads only, and there is no verify control here.** `POST /v1/audit/verify-chain` exists and the CLI
 * exposes it, but a verification consumes a worker and is submitted by a schedule every 24 hours; a
 * button beside a listing invites somebody to submit one per curiosity. The screen shows what the
 * nightly walk found, which is the question an auditor actually has.
 *
 * **Paging is the platform's own signed cursor and this module never constructs one.** The token covers
 * the tenant *and* a fingerprint of the query, so changing a filter invalidates it by design — which is
 * why `useAuditEntries` resets its cursor stack whenever the filters change rather than reusing one.
 * The API would answer 400; resetting is the honest response to a question that became a different
 * question.
 */

import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { isAuditEntry } from '@/shared/api/generated/audit';
import type { AuditEntry } from '@/shared/api/generated/audit';
import { getPage } from '@/shared/api/http';
import type { Page } from '@/shared/api/http';

/** What an auditor can narrow by. Mirrors the contract's query parameters exactly.
 *
 * `undefined` means "not filtering", never an empty string: an empty `actor` sent to the API is a
 * literal filter value and would match nothing, returning a page that reads as "this tenant did
 * nothing" — the wrong answer to give an auditor.
 */
export interface AuditFilters {
  readonly actor?: string | undefined;
  readonly action?: string | undefined;
  readonly aggregateType?: string | undefined;
  /** One aggregate's own history — a single connection, rule or dataset.
   *
   * The route has always taken this and nothing sent it, so the chain could answer "what has
   * happened to *this* source" and no screen could ask. It is what a per-record activity panel is
   * built on, and it is paired with `aggregateType` at every call site rather than used alone: an id
   * is unique across the platform in practice and the pair is what makes the filter mean "this
   * connection" rather than "whatever happens to share this uuid".
   */
  readonly aggregateId?: string | undefined;
  readonly occurredFrom?: string | undefined;
  readonly occurredTo?: string | undefined;
}

export const ENTRIES_PER_PAGE = 50;

/* How many entries the front page's activity feed shows.
 *
 * Five, and the bound is on the *request* rather than a slice of a 50-row page: the audit chain is
 * the one listing in this product where a page carries an actor and an action for every row, so
 * fetching ten times what is rendered means ten times the identity data crossing the wire for a
 * card nobody scrolls. */
export const RECENT_ENTRIES = 5;

/* How many entries a single record's activity panel shows.
 *
 * Ten, against the front page's five: this panel is the whole of what a reader can learn about one
 * source's history, where the dashboard's is a glance. Bounded for the same reason, and the screen
 * says the list is the most recent ten rather than implying it is all of them — there is no pager
 * here, and a panel that truncated silently would be the `next_cursor: null`-while-truncating defect
 * in a card.
 */
export const ACTIVITY_ENTRIES = 10;

export function auditEntriesUrl(
  filters: AuditFilters,
  cursor: string | null,
  limit: number = ENTRIES_PER_PAGE,
): string {
  const params = new URLSearchParams({ limit: String(limit) });
  if (filters.actor) params.set('actor', filters.actor);
  if (filters.action) params.set('action', filters.action);
  if (filters.aggregateType) params.set('aggregate_type', filters.aggregateType);
  if (filters.aggregateId) params.set('aggregate_id', filters.aggregateId);
  if (filters.occurredFrom) params.set('occurred_from', filters.occurredFrom);
  if (filters.occurredTo) params.set('occurred_to', filters.occurredTo);
  if (cursor !== null) params.set('after', cursor);
  return `/v1/audit/entries?${params.toString()}`;
}

/** One page of the chain.
 *
 * The cursor is part of the query key, so React Query caches each page separately and going back is
 * instant rather than a refetch. The filters are in the key too — a cached page from a different
 * filter set would be a page the current question did not ask for.
 */
export function useAuditEntries(
  filters: AuditFilters,
  cursor: string | null,
  limit: number = ENTRIES_PER_PAGE,
): UseQueryResult<Page<AuditEntry>> {
  return useQuery({
    /* The limit is in the key. Without it the front page's five-row read and the audit screen's
     * fifty-row read would share a cache entry, so whichever mounted first would decide what the
     * other got — the shared-cache-key coupling `useDatasetNames` had to be untangled from, where
     * two consumers of one key wanted different URLs. */
    queryKey: ['audit', 'entries', filters, cursor, limit],
    queryFn: () =>
      getPage(auditEntriesUrl(filters, cursor, limit), isAuditEntry, 'audit entry'),
  });
}

/* The most recent handful, for the front page's activity feed.
 *
 * Unfiltered and uncursored on purpose: this is "what just happened", so any filter would make it a
 * question the reader did not ask. A separate function rather than a call with three arguments at the
 * call site, so the front page cannot accidentally acquire a filter.
 */
export function useRecentActivity(): UseQueryResult<Page<AuditEntry>> {
  return useAuditEntries({}, null, RECENT_ENTRIES);
}

/** What the audit chain records under one aggregate, newest first.
 *
 * **The chain is the source of truth for a per-record activity panel, and no new table was
 * invented for one.** Every mutation in this platform already writes an entry naming the actor, the
 * action and the instant; a second store would be a second answer to "what happened here" that
 * could disagree with the one an auditor reads.
 *
 * **What it deliberately cannot say is what changed.** The listing route does not select `payload`
 * — the snapshot never leaves PostgreSQL on this path — so a panel built on this reports that
 * something happened, to what, and by whom, and never its contents. A screen must say so rather
 * than leave a reader inferring that entries happen to have no detail.
 *
 * `ACTIVITY_ENTRIES` bounds the request rather than slicing a fifty-row page: this is the one
 * listing in the product where every row carries an actor, so fetching ten times what is rendered
 * means ten times the identity data crossing the wire for a panel nobody scrolls.
 */
/** The most recent entry recording one *named action* against one aggregate.
 *
 * **A second request rather than a scan of the activity page, and the reason is the bound.**
 * `useAggregateActivity` fetches the ten newest entries; a source's registration is its *oldest*,
 * so on a connection that has been verified a dozen times the entry would fall off the page and the
 * card would silently stop saying who registered it. Filtering server-side answers exactly the
 * question — "when was this registered, and by whom" — in one row, whatever the history since.
 *
 * `limit: 1` because the actions this is used for happen once per aggregate. If it is ever pointed at
 * a repeating action it returns the latest, which is the only sensible reading of "the entry for this
 * action" and is stated so nobody has to infer it.
 */
export function useAggregateAction(
  aggregateType: string,
  aggregateId: string | null,
  action: string,
): UseQueryResult<Page<AuditEntry>> {
  const filters: AuditFilters = {
    aggregateType,
    aggregateId: aggregateId ?? undefined,
    action,
  };
  return useQuery({
    queryKey: ['audit', 'entries', filters, null, 1],
    enabled: aggregateId !== null && aggregateId !== '',
    queryFn: () =>
      getPage(auditEntriesUrl(filters, null, 1), isAuditEntry, 'audit entry'),
  });
}

export function useAggregateActivity(
  aggregateType: string,
  aggregateId: string | null,
  limit: number = ACTIVITY_ENTRIES,
): UseQueryResult<Page<AuditEntry>> {
  const filters: AuditFilters = {
    aggregateType,
    aggregateId: aggregateId ?? undefined,
  };
  return useQuery({
    queryKey: ['audit', 'entries', filters, null, limit],
    /* `enabled` rather than a guard inside the function: a panel mounts before its record's id is
     * known, and a request with no `aggregate_id` would return the *whole tenant's* chain filtered
     * only by type — a listing of every source's history rendered as one source's. */
    enabled: aggregateId !== null && aggregateId !== '',
    queryFn: () =>
      getPage(auditEntriesUrl(filters, null, limit), isAuditEntry, 'audit entry'),
  });
}
